import { ILabelDataHandler } from '../../data-handlers/label/ILabelDataHandler'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import Notification from '../../entities/notification/Notification'
import { store } from '../../services/store'
import { setNotification } from '../../services/store/actions/notifications/NotificationsAction'
import FileFormats from '../../constants/FileFormats'
export class LabelRepository {
  handler: ILabelDataHandler
  _BTPrinter: any

  constructor(handler: ILabelDataHandler) {
    this.handler = handler

    if('BTPrinter' in window) {
      this._BTPrinter = window.BTPrinter
    }
  }

  async createBatch(products: OrderProduct[]): Promise<void> {
    const label = await this.handler.createBatch(products)

    this.printDocument(products[0].order.orderId, label, products[0].itemId)
  }

  remove(product: OrderProduct): Promise<void> {
    return this.handler.remove(product)
  }

  removeBatch(products: OrderProduct[]): Promise<void> {
    return this.handler.removeBatch(products)
  }

  async print(product: OrderProduct) {
    const label = await this.handler.create(product)
    this.printDocument(product.order.orderId, label, product.itemId)
  }

  async rePrint(product: OrderProduct) {
    if (product.label) {
      this.printDocument(product.order.orderId, product.label, product.itemId)
    } else {
      throw new Error('Attempting to reprint product with empty label')
    }
  }

  async printDocument(orderId: number, label: string, itemId: number) {
    const {fileContent, fileType} = await this.handler.getDocumentBase64ByLabel(orderId, label, itemId)
    const decodedFileContent = atob(fileContent)

    if (fileType === FileFormats.ZPL) {
      this.printDocumentByZpl(decodedFileContent)
    } else {
      const documentUrl = await this.getDocumentUrl(decodedFileContent)
      this.printDocumentByUrl(documentUrl)
    }
  }

  async getDocumentUrl(fileContent: string): Promise<string> {
    try {
      const byteArray = Uint8Array.from(
        fileContent.split('').map(char => char.charCodeAt(0))
      )

      const documentBlob = new Blob([byteArray], { type: 'application/pdf' })

      return URL.createObjectURL(documentBlob)

    } catch (error) {
      console.error(error)
      throw new Error('Can`t get document url')
    }
  }

  setPrinterNotification(id: string, text: string) {
    store.dispatch(setNotification(new Notification({
      id,
      text,
    })))
  }

  printDocumentByZpl(documentZpl: string) {
    const printer = this._BTPrinter

    printer.status((isActiveBluetooth: boolean) => {
      if (isActiveBluetooth) {
        printer.list((deviceInfo: Array<string>) => {
          const deviceName: string = deviceInfo[0]
          printer.connect((): void => {
            printer.connected((isPrinterConnected: boolean) => {
              if (isPrinterConnected) {
                printer.printText(() => {
                  setTimeout(() => {
                    printer.disconnect(() => {
                    }, () => {
                      this.setPrinterNotification('printer_not_disconnected', 'Printer is not disconnected')
                    }, deviceName)
                  }, 1500)
                }, () => {
                  this.setPrinterNotification('text_printing_error', 'Text is not printing')
                }, documentZpl)
              } else {
                this.setPrinterNotification('printer_connection_error', 'Printer is not connected')
              }
            })
          }, () => {
            this.setPrinterNotification('printer_connection_error', 'Printer is not connected')
          }, deviceName)
        })
      } else {
        this.setPrinterNotification('bluetooth_disabled', 'Please enable bluetooth')
      }
    }, () => {
      this.setPrinterNotification('printer_status', 'Unknown printer status')
    })
  }

  printDocumentByUrl(documentUrl: string): void {
    try {
      const pdfWindow = window.open(documentUrl)

      if (pdfWindow) {
        pdfWindow.focus()
        pdfWindow.print()
      } else {
        throw new Error('Can`t print document')
      }
    } catch (error) {
      console.error(error)
      throw new Error('Can`t open or print document')
    } finally {
      URL.revokeObjectURL(documentUrl)
    }
  }
}

declare global {
  interface Window { BTPrinter: any; }
}
